import { useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity,
  LayoutChangeEvent, NativeSyntheticEvent, NativeScrollEvent,
} from 'react-native';
import { BarChart, LineChart } from 'react-native-gifted-charts';
import { useRetentionTrend } from '@/hooks/useRetentionTrend';
import { CATEGORY_COLOR, CATEGORY_LABEL, CATEGORY_ORDER, RetentionCategory } from '@/lib/retention';

const WEEKS = 8;
const BAR_WIDTH = 22;
const BAR_SPACING = 10;
const Y_AXIS_PAD = 50;
const MIN_CHART_WIDTH = WEEKS * (BAR_WIDTH + BAR_SPACING) + Y_AXIS_PAD;

type ChartMode = 'bars' | 'lines';

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

export function RetentionTrendChart({ personalId }: { personalId: string }) {
  const { data: trends = [], isLoading } = useRetentionTrend(personalId, WEEKS);
  const [mode, setMode] = useState<ChartMode>('bars');
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [scrollX, setScrollX] = useState(0);

  if (isLoading) return <ActivityIndicator color="#C8102E" />;
  if (trends.length === 0) {
    return <Text style={styles.empty}>Sem dados suficientes pra gerar tendência.</Text>;
  }

  function onLayout(e: LayoutChangeEvent) {
    setContainerWidth(e.nativeEvent.layout.width);
  }

  function onScroll(e: NativeSyntheticEvent<NativeScrollEvent>) {
    setScrollX(e.nativeEvent.contentOffset.x);
  }

  // chartWidth garante que o gráfico não fica esmagado em containers pequenos.
  const chartWidth = Math.max(containerWidth - Y_AXIS_PAD, MIN_CHART_WIDTH - Y_AXIS_PAD);

  // Scrollbar custom: só aparece se há overflow real (medido via onContentSizeChange).
  const hasOverflow = contentWidth > containerWidth + 1;
  const visibleRatio = contentWidth > 0 ? Math.min(1, containerWidth / contentWidth) : 1;
  const thumbWidth = Math.max(24, visibleRatio * containerWidth);
  const maxScroll = Math.max(1, contentWidth - containerWidth);
  const thumbLeft = (scrollX / maxScroll) * (containerWidth - thumbWidth);

  const chart = mode === 'bars'
    ? <BarsView trends={trends} width={chartWidth} />
    : <LinesView trends={trends} width={chartWidth} />;

  return (
    <View onLayout={onLayout}>
      {/* Toggle bars/lines */}
      <View style={styles.modeRow}>
        <ModeBtn label="Barras" active={mode === 'bars'} onPress={() => setMode('bars')} />
        <ModeBtn label="Linhas" active={mode === 'lines'} onPress={() => setMode('lines')} />
      </View>

      {containerWidth > 0 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chartWrap}
          onScroll={onScroll}
          onContentSizeChange={(w) => setContentWidth(w)}
          scrollEventThrottle={16}
        >
          {chart}
        </ScrollView>
      )}

      {hasOverflow && (
        <>
          <View style={styles.scrollbarTrack}>
            <View style={[styles.scrollbarThumb, { width: thumbWidth, left: thumbLeft }]} />
          </View>
          <Text style={styles.scrollHint}>← arraste para ver as 8 semanas →</Text>
        </>
      )}

      {/* Legenda */}
      <View style={styles.legend}>
        {CATEGORY_ORDER.map(cat => (
          <View key={cat} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: CATEGORY_COLOR[cat] }]} />
            <Text style={styles.legendText}>{CATEGORY_LABEL[cat]}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function ModeBtn({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <TouchableOpacity
      style={[styles.modeBtn, active && styles.modeBtnActive]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[styles.modeBtnText, active && styles.modeBtnTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

type Trend = { weekStart: string; counts: Record<RetentionCategory, number> };

function BarsView({ trends, width }: { trends: Trend[]; width: number }) {
  const stackData = trends.map(w => ({
    stacks: CATEGORY_ORDER.map(cat => ({
      value: w.counts[cat],
      color: CATEGORY_COLOR[cat],
    })).filter(s => s.value > 0),
    label: shortDate(w.weekStart),
  }));

  return (
    <BarChart
      stackData={stackData}
      width={width}
      height={180}
      barWidth={22}
      spacing={10}
      xAxisLabelTextStyle={{ color: '#888', fontSize: 9 }}
      yAxisTextStyle={{ color: '#888', fontSize: 9 }}
      noOfSections={4}
      rulesColor="#EEE"
    />
  );
}

function LinesView({ trends, width }: { trends: Trend[]; width: number }) {
  const dataSets = CATEGORY_ORDER.map(cat => ({
    data: trends.map(w => ({
      value: w.counts[cat],
      label: shortDate(w.weekStart),
    })),
    color: CATEGORY_COLOR[cat],
    dataPointsColor: CATEGORY_COLOR[cat],
    thickness: 2,
  }));

  // Spacing entre pontos calculado pra caber dentro da `width` recebida.
  // LineChart default tem initialSpacing/endSpacing altos — explícitos pra evitar overflow.
  const initial = 10;
  const end = 10;
  const spacing = Math.max(20, (width - initial - end) / Math.max(1, trends.length - 1));

  return (
    <LineChart
      dataSet={dataSets}
      data={dataSets[0].data}
      width={width}
      height={180}
      color={dataSets[0].color}
      thickness={2}
      initialSpacing={initial}
      endSpacing={end}
      spacing={spacing}
      xAxisLabelTextStyle={{ color: '#888', fontSize: 9 }}
      yAxisTextStyle={{ color: '#888', fontSize: 9 }}
      hideDataPoints={false}
      noOfSections={4}
      rulesColor="#EEE"
      curved
    />
  );
}

const styles = StyleSheet.create({
  modeRow: { flexDirection: 'row', gap: 6, marginTop: 4, marginBottom: 8 },
  modeBtn: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14, backgroundColor: '#EEE' },
  modeBtnActive: { backgroundColor: '#C8102E' },
  modeBtnText: { fontSize: 11, color: '#555', fontWeight: '600' },
  modeBtnTextActive: { color: '#fff' },

  chartWrap: { borderRadius: 6, marginBottom: 8 },
  scrollbarTrack: {
    height: 6, borderRadius: 3, backgroundColor: '#EEE',
    marginTop: 4, marginBottom: 4, position: 'relative',
  },
  scrollbarThumb: {
    position: 'absolute', top: 0, height: 6, borderRadius: 3,
    backgroundColor: '#C8102E',
  },
  scrollHint: { fontSize: 10, color: '#aaa', textAlign: 'center', marginBottom: 4 },
  empty: { fontSize: 13, color: '#888', textAlign: 'center', padding: 20 },
  legend: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, paddingTop: 8 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendText: { fontSize: 11, color: '#555' },
});
